/**
 * One place for the rules every window-level key handler in the dashboard needs.
 *
 * Each view and drawer tab used to write its own guard, and they drifted: some forgot
 * `<select>` (so the wave picker in the command bar could not be driven with the arrow keys
 * on most screens), and none of them checked *where* the focus was, so a list handler bound
 * to `window` acted on Enter while the operator was tabbing through the drawer's own
 * buttons — pressing Enter on "Close" opened a run instead.
 *
 * Everything here duck-types the event target rather than testing `instanceof HTMLElement`,
 * so the rules are unit-testable in the node gate instead of only in a browser.
 */

/** The bit of an event target these rules read. */
export interface KeyTarget {
  tagName?: string;
  isContentEditable?: boolean;
  closest?: (selector: string) => unknown;
}

function asTarget(target: unknown): KeyTarget | null {
  if (target === null || typeof target !== 'object') return null;
  return target as KeyTarget;
}

/**
 * Focus is in a field, so single-letter shortcuts and list navigation must stay out of it.
 *
 * `<select>` counts: its own arrow keys pick an option, and stealing them makes the control
 * unusable with the keyboard.
 */
export function isTypingTarget(target: unknown): boolean {
  const element = asTarget(target);
  if (!element) return false;
  const tag = typeof element.tagName === 'string' ? element.tagName.toUpperCase() : '';
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable === true
  );
}

/** True when the shortcut should be ignored: a field has focus, or a modifier is held. */
export function shortcutBlocked(
  event: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'altKey'> & { target?: unknown },
): boolean {
  return isTypingTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey;
}
